import { Component, input, OnDestroy, OnInit } from '@angular/core';
import {
	FormBuilder,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { AsyncSubject, take, takeUntil } from 'rxjs';
import { ModalMoverComponent } from 'src/lib/templates/global/modal-mover/modal-mover.component';
import { isNumber } from 'src/lib/utilities/compare';
import { IPaginationTable } from '../../core/pagination-table.interface';

interface FormGroupModel {
	type: FormControl<'add' | 'replace'>;
	ids: FormControl<string>;
}

@Component({
	selector: 'ae-pagination-table-bulk-select-modal',
	imports: [ModalMoverComponent, ReactiveFormsModule],
	templateUrl: './pagination-table-bulk-select-modal.component.html',
	styleUrl: './pagination-table-bulk-select-modal.component.scss',
})
export class PaginationTableBulkSelectModalComponent<T>
	implements OnDestroy, OnInit
{
	private _unsubscribe$ = new AsyncSubject<null>();

	readonly pgTable = input.required<IPaginationTable<T>>();

	public title = 'Bulk Select';

	public form: FormGroup<FormGroupModel>;
	private allTableStringIds: string[];
	private isNumberId: boolean = false;

	constructor(
		public activeModal: NgbActiveModal,
		private fb: FormBuilder,
		private toastr: ToastrService,
	) {}

	ngOnInit(): void {
		this.form = this.fb.group<FormGroupModel>({
			ids: new FormControl(null, Validators.required),
			type: new FormControl(null, Validators.required),
		});

		const pgTable = this.pgTable();
		pgTable
			.tableConfig()
			.selection.getAllKeys$(pgTable.tableConfig().search.form)
			.pipe(take(1), takeUntil(this._unsubscribe$))
			.subscribe((x) => {
				if (isNumber(x[0])) {
					this.isNumberId = true;
					this.allTableStringIds = x.map((v) => v.toString());
				} else {
					this.allTableStringIds = x as string[];
				}
			});
	}

	ngOnDestroy(): void {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}

	public close = () => {
		const values = this.form.value;

		const parsedIds =
			values.ids
				?.split('\n')
				.map((id) => id.trim())
				.filter((id) => id.length > 0) || [];

		const invalidIds = [];

		parsedIds.forEach((id) => {
			if (!this.allTableStringIds.includes(id)) {
				invalidIds.push(id);
			}
		});

		const validIds = parsedIds
			.filter((x) => !invalidIds.includes(x))
			.map((x) => (this.isNumberId ? parseInt(x, 10) : x));

		const pgTable = this.pgTable();
		if (values.type === 'replace') {
			pgTable.selectedItems = [...new Set([...validIds])];
		} else {
			pgTable.selectedItems = [
				...new Set([...pgTable.selectedItems, ...validIds]),
			];
		}

		if (invalidIds.length > 0) {
			this.toastr.warning(
				`Not all of the pasted IDs were able to be selected. We could not find ${invalidIds.length} IDs in the table.`,
				this.title,
			);
		} else {
			this.toastr.success('Selections updated', this.title);
		}

		this.activeModal.close();
	};
}
